import type { ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import { useGoogleAnalyticsDispatcher } from 'hooks/useGoogleAnalyticsDispatcher';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import { TertiaryButton } from '@atoms/buttons/TertiaryButton';
import { DownloadIcon } from '@atoms/Icon/DownloadIcon';

import { useDecodedPreviewURL } from './hooks';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

export const Preview = () => {
  const trackEvent = useGoogleAnalyticsDispatcher();

  const { error: urlDecodeError, data } = useDecodedPreviewURL();

  const [documentLoadError, setDocumentLoadError] = useState<boolean>(false);

  const [numPages, setNumPages] = useState<number>();

  const documentOptions: ComponentProps<typeof Document>['options'] = useMemo(
    () => ({
      stopAtErrors: true,
      isEvalSupported: false
    }),
    []
  );

  if (urlDecodeError || documentLoadError) {
    trackEvent('pdf_document_preview_error', {
      product: data?.filename || 'unknown',
      urlDecodeError,
      documentLoadError
    });
    return (
      <div className="size-full bg-gray-800 pt-10 text-center text-white">
        <div className="text-xl">Failed to load document preview</div>
        <div>Please try to generate the document again</div>
      </div>
    );
  }

  return (
    <div className="flex size-full min-h-min flex-col items-center gap-5 bg-gray-800 py-5">
      <div className="w-full text-center text-white">
        <div className="p-2 text-xl">PREVIEW</div>
        <TertiaryButton
          className="rounded border border-content-brand-faint bg-action-primary text-white"
          onClick={() =>
            trackEvent('pdf_document_downloaded', { product: data.product })
          }
          download={data.filename}
          href={data.url}
        >
          Download
          <DownloadIcon className="size-4 stroke-current" />
        </TertiaryButton>
      </div>
      <Document
        onLoadError={() => setDocumentLoadError(true)}
        options={documentOptions}
        file={data.url}
        onLoadSuccess={(doc) => setNumPages(doc.numPages)}
        className="mt-3 max-w-min"
      >
        {Array.from(new Array(numPages), (_, page) => (
          <Page key={`page_${page}`} pageNumber={page + 1} />
        ))}
      </Document>
    </div>
  );
};
